import { toKebabCase } from "#/shared/utils/string";
import { isPresent } from "#ce/utils/props";

/**
 * Create a list of modifiers based on the keys and the props.
 * It will create a list of modifiers based on the keys and the props.
 *
 * Boolean modifiers (`withValue` false) are resolved through {@link isPresent},
 * so a custom-element string attribute like `block="false"` (which arrives as the
 * truthy string `"false"`) correctly produces NO modifier. Value modifiers
 * (`withValue` true) keep raw truthiness: a string value like `size="sm"` is
 * emitted, while an empty/absent value is skipped.
 *
 * @param {Object} props
 * @param {Array<String>} keys
 * @param {Object} options
 * @param {Boolean} options.withValue
 * @returns Object
 */
export function createModifiers(props, keys, options = {}) {
  const withValue = options?.withValue ?? false;
  const modifiers = [];

  for (const key of keys) {
    const value = props[key];

    if (withValue) {
      if (!value) {
        continue;
      }
      modifiers.push(createModifier(key, value));
    } else {
      if (!isPresent(value)) {
        continue;
      }
      modifiers.push(createModifier(key));
    }
  }

  return modifiers;
}

/**
 * Create a modifier based on the segments.
 * It sanitizes the segments and joins them with a separator.
 *
 * @param  {...String} segments
 * @returns String
 *
 * @example
 * createModifier("size", "sm") // "--size-sm"
 * createModifier("color", "red", "special case") // "--color-red-special-case"
 * createModifier("some value") // "--some-value"
 *
 */
function createModifier(...segments) {
  const separator = "-";
  return `--${segments.map(toKebabCase).join(separator).replace(/\s/g, separator)}`;
}
